// BASIC Prediction Types
// Matches backend response structure from Agent 3 (V1) and V2

// V1 Status values (legacy)
export type BasicStatusV1 = 'EXCELLENT' | 'SAFE' | 'WARNING' | 'CRITICAL';

// V2 Status values (current)
export type BasicStatusV2 = 'GOOD' | 'WARNING' | 'INSUFFICIENT' | 'ELIMINATED';

// Combined type for compatibility
export type BasicStatus = BasicStatusV1 | BasicStatusV2;

export interface BasicPrediction {
  measure: number;
  percentile: number;
  threshold: number;
  status: BasicStatus;
  confidenceScore: number;
  calculatedAt: string;
  dataSource?: 'official' | 'calculated';
  isMonitored?: boolean;
  cacheExpires?: string;
}

export interface AllBasicPredictions {
  dotNumber: number;
  // V2 uses 'basics' instead of 'predictions'
  basics?: {
    'BASIC-11': BasicPrediction;
    'BASIC-12': BasicPrediction;
    'BASIC-13': BasicPrediction;
    'BASIC-14': BasicPrediction;  // Controlled Substances/Alcohol
    'BASIC-15': BasicPrediction;
    'BASIC-16': BasicPrediction;  // Crash Indicator
    'BASIC-17': BasicPrediction;  // Hazmat Compliance
  };
  // V1 compatibility (deprecated)
  predictions?: {
    'BASIC-11': BasicPrediction;
    'BASIC-12': BasicPrediction;
    'BASIC-13': BasicPrediction;
    'BASIC-14': BasicPrediction;
    'BASIC-15': BasicPrediction;
    'BASIC-16': BasicPrediction;
    'BASIC-17': BasicPrediction;
  };
}

export interface HypotheticalViolation {
  cfrCode: string;
  violationDate: string;
  basicId: string;
  severity: number;
  oos: boolean;
}

export interface ViolationImpact {
  basicId: string;
  currentPrediction: BasicPrediction;
  newPrediction: {
    measure: number;
    percentile: number;
    threshold: number;
    status: BasicStatus;
  };
  change: {
    measureDelta: number;
    percentileDelta: number;
    statusChange: boolean;
    riskIncrease: string;
  };
}

export interface WhatIfResponse {
  dotNumber: number;
  impacts: ViolationImpact[];
  summary: {
    totalImpacts: number;
    statusChanges: number;
    averagePercentileIncrease: number;
    worstCaseStatus: BasicStatus;
  };
}

export interface TrajectoryPoint {
  date: Date | string;
  monthsFromNow: number;
  measure: number;
  percentile: number | null;
  status: string;
  violationsAgedOut: number;
  remainingViolations: number;
}

export interface TrajectoryResponse {
  dotNumber: number;
  basicId: string;
  currentMeasure: number;
  currentPercentile: number | null;
  trajectory: TrajectoryPoint[];
  projectionMonths: number;
  trend: 'IMPROVING' | 'STABLE' | 'DECLINING';
  estimatedImprovement: number;
  recommendations: string[];
}

export interface SingleImpactRequest {
  cfrCode: string;
  violationDate: string;
  oos: boolean;
}

export interface SingleImpactResponse {
  dotNumber: number;
  basicId: string;
  currentPrediction: BasicPrediction;
  impactedPrediction: {
    measure: number;
    percentile: number;
    threshold: number;
    status: BasicStatus;
  };
  change: {
    measureDelta: number;
    percentileDelta: number;
    statusChange: boolean;
    riskIncrease: string;
  };
  violationDetails: {
    cfrCode: string;
    violationDate: string;
    severity: number;
    oos: boolean;
  };
}

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  timestamp?: string;
}

export interface BasicHistoryPoint {
  date: string;
  measure: number;
  percentile: number | null;
}

export interface BasicHistoryResponse {
  dotNumber: number;
  basicId: string;
  weeks: number;
  fromCache: boolean;
  points: BasicHistoryPoint[];
}

// BASIC Categories metadata
export const BASIC_CATEGORIES = {
  'BASIC-11': {
    name: 'Unsafe Driving',
    threshold: 65,
    description: 'Operation of commercial motor vehicles by drivers in a dangerous or careless manner',
  },
  'BASIC-12': {
    name: 'Hours of Service',
    threshold: 65,
    description: 'Operation of commercial motor vehicles by fatigued drivers',
  },
  'BASIC-13': {
    name: 'Driver Fitness',
    threshold: 80,
    description: 'Operation of commercial motor vehicles by unqualified drivers',
  },
  'BASIC-14': {
    name: 'Controlled Substances/Alcohol',
    threshold: 80,
    description: 'Operation of commercial motor vehicles while impaired by alcohol or drugs',
    note: 'FMCSA never publishes this data publicly for privacy reasons',
  },
  'BASIC-15': {
    name: 'Vehicle Maintenance',
    threshold: 80,
    description: 'Failure to properly maintain commercial motor vehicles',
  },
  'BASIC-16': {
    name: 'Crash Indicator',
    threshold: 65,
    description: 'Crash rate and severity for commercial motor carriers',
    note: 'Calculated from crash database (last 24 months)',
  },
  'BASIC-17': {
    name: 'Hazmat Compliance',
    threshold: 80,
    description: 'Hazardous materials transportation violations',
    note: 'Only calculated for carriers with hazmat operations',
  },
} as const;

export type BasicId = keyof typeof BASIC_CATEGORIES;

// Status badge colors (V1 and V2)
export const STATUS_COLORS = {
  // V1 Status colors
  EXCELLENT: { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
  SAFE: { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
  WARNING: { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
  CRITICAL: { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' },
  // V2 Status colors
  GOOD: { bg: '#d1fae5', text: '#065f46', border: '#10b981' },        // Green (same as EXCELLENT)
  INSUFFICIENT: { bg: '#f3f4f6', text: '#4b5563', border: '#9ca3af' }, // Gray
  ELIMINATED: { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' },   // Light gray
} as const;

// Helper functions
export const getStatusColor = (status: BasicStatus) => {
  // Handle null/undefined status
  if (!status) {
    return { bg: '#f3f4f6', text: '#4b5563', border: '#9ca3af' }; // Default gray
  }
  return STATUS_COLORS[status] || { bg: '#f3f4f6', text: '#4b5563', border: '#9ca3af' };
};

export const getBasicName = (basicId: BasicId): string => {
  return BASIC_CATEGORIES[basicId].name;
};

export const getBasicThreshold = (basicId: BasicId): number => {
  return BASIC_CATEGORIES[basicId].threshold;
};

export const calculateProgressPercentage = (percentile: number, threshold: number): number => {
  return Math.min((percentile / threshold) * 100, 100);
};

export const getPointsFromThreshold = (percentile: number, threshold: number): number => {
  return threshold - percentile;
};
